import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Tabs,
    Card,
    Form,
    Input,
    Row,
    Col,
    Radio,
    Table,
    Button,
    DatePicker,
} from "antd";

import {
    User,
    Activity,
    ClipboardList,
    Scissors,
    Save,
} from "lucide-react";

import { useState } from "react";
import dayjs from "dayjs";
import Layout from '../../Layout'

const { TextArea } = Input;

interface HealthRecord {
    key: number;
    name: string;
}

export default function CosmeticSurgeryReception() {
  const { t } = useTranslation();
    const [form] = Form.useForm();

    const healthList: HealthRecord[] = [
        { key: 1, name: t('cosmetic_surgery.health_using_medicine') },
        { key: 2, name: t('cosmetic_surgery.health_diabetes') },
        { key: 3, name: t('cosmetic_surgery.health_cardiovascular') },
        { key: 4, name: t('cosmetic_surgery.health_kidney_liver') },
        { key: 5, name: t('cosmetic_surgery.health_thyroid') },
        { key: 6, name: t('cosmetic_surgery.health_anesthesia') },
        { key: 7, name: t('cosmetic_surgery.health_pregnant') },
        { key: 8, name: t('cosmetic_surgery.health_allergy') },
        { key: 9, name: t('cosmetic_surgery.health_blood_pressure') },
        { key: 10, name: t('cosmetic_surgery.health_blood_clot') },
        { key: 11, name: t('cosmetic_surgery.health_hemorrhage') },
        { key: 12, name: t('cosmetic_surgery.health_hepatitis') },
        { key: 13, name: t('cosmetic_surgery.health_immune') },
        { key: 14, name: t('cosmetic_surgery.health_infection') },
        { key: 15, name: t('cosmetic_surgery.health_epilepsy') },
        { key: 16, name: t('cosmetic_surgery.health_pacemaker') },
        { key: 17, name: t('cosmetic_surgery.health_wound') },
        { key: 18, name: t('cosmetic_surgery.health_surgery') },
        { key: 19, name: t('cosmetic_surgery.health_psoriasis') },
        { key: 20, name: t('cosmetic_surgery.health_other') },
    ];

    const [healthState, setHealthState] = useState<Record<number, string>>(
        healthList.reduce((acc, item) => {
            acc[item.key] = "no";
            return acc;
        }, {} as Record<number, string>)
    );

    const columns = [
        {
            title: t('cosmetic_surgery.col_disease'),
            dataIndex: "name",
        },

        {
            title: t('cosmetic_surgery.col_yes'),
            width: 80,
            align: "center" as const,
            render: (_: any, record: HealthRecord) => (
                <Radio
                    checked={healthState[record.key] === "yes"}
                    onChange={() =>
                        setHealthState((prev) => ({
                            ...prev,
                            [record.key]: "yes",
                        }))
                    }
                />
            ),
        },

        {
            title: t('cosmetic_surgery.col_no'),
            width: 80,
            align: "center" as const,
            render: (_: any, record: HealthRecord) => (
                <Radio
                    checked={healthState[record.key] === "no"}
                    onChange={() =>
                        setHealthState((prev) => ({
                            ...prev,
                            [record.key]: "no",
                        }))
                    }
                />
            ),
        },

        {
            title: t('cosmetic_surgery.col_explain'),
            render: (_: any, record: HealthRecord) => (
                <Input disabled={healthState[record.key] !== "yes"} />
            ),
        },
    ];

    const items = [
        {
            key: "1",
            label: (
                <span className="flex items-center gap-2">
                    <User size={16} />
                    {t('cosmetic_surgery.tab_info')}
                </span>
            ),

            children: (
                <Card className="dark:bg-[#1f1f1f] shadow-sm">
                    <Form layout="vertical" form={form}>
                        {/* THÔNG TIN CÁ NHÂN */}

                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3 font-semibold text-gray-700 dark:text-gray-200">
                                <User size={16} />
                                {t('cosmetic_surgery.personal_info')}
                            </div>

                            <div className="border rounded-lg p-4 dark:border-gray-700">
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('cosmetic_surgery.full_name')} name="name">
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('cosmetic_surgery.gender')}>
                                            <Radio.Group>
                                                <Radio value="male">{t('cosmetic_surgery.male')}</Radio>
                                                <Radio value="female">{t('cosmetic_surgery.female')}</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('cosmetic_surgery.dob')}>
                                            <DatePicker className="w-full" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('cosmetic_surgery.phone')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col span={24}>
                                        <Form.Item label={t('cosmetic_surgery.address')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('cosmetic_surgery.job')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('cosmetic_surgery.referral')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        </div>

                        {/* TÌNH TRẠNG & NHU CẦU */}

                        <div>
                            <div className="flex items-center gap-2 mb-3 font-semibold text-gray-700 dark:text-gray-200">
                                <ClipboardList size={16} />
                                {t('cosmetic_surgery.status_needs')}
                            </div>

                            <div className="border rounded-lg p-4 dark:border-gray-700">
                                <Form.Item label={t('cosmetic_surgery.current_status')}>
                                    <TextArea rows={3} />
                                </Form.Item>

                                <Form.Item label={t('cosmetic_surgery.improvement_needs')}>
                                    <TextArea rows={3} />
                                </Form.Item>
                            </div>
                        </div>
                    </Form>

                    <div className="flex justify-end mt-4">
                        <Button type="primary" icon={<Save size={16} />}>
                            {t('cosmetic_surgery.save')}
                        </Button>
                    </div>
                </Card>
            ),
        },

        {
            key: "2",
            label: (
                <span className="flex items-center gap-2">
                    <Activity size={16} />
                    {t('cosmetic_surgery.tab_health')}
                </span>
            ),

            children: (
                <Card className="dark:bg-[#1f1f1f] shadow-sm">
                    <Table
                        columns={columns}
                        dataSource={healthList}
                        pagination={false}
                        className="custom-ant-table"
                        scroll={{ x: "max-content" }}
                    />

                    <div className="flex justify-end mt-4">
                        <Button type="primary" icon={<Save size={16} />}>
                            {t('cosmetic_surgery.save')}
                        </Button>
                    </div>
                </Card>
            ),
        },

        {
            key: "3",
            label: (
                <span className="flex items-center gap-2">
                    <ClipboardList size={16} />
                    {t('cosmetic_surgery.tab_doctor')}
                </span>
            ),

            children: (
                <Card className="dark:bg-[#1f1f1f] shadow-sm">
                    <Row gutter={24}>
                        <Col xs={24} md={12}>
                            <Form layout="vertical">
                                <Form.Item label={t('cosmetic_surgery.doctor_current_status')}>
                                    <TextArea rows={2} />
                                </Form.Item>

                                <Form.Item label={t('cosmetic_surgery.diagnosis')}>
                                    <Input />
                                </Form.Item>

                                <Form.Item label={t('cosmetic_surgery.treatment_method')}>
                                    <TextArea rows={2} />
                                </Form.Item>

                                <Form.Item label={t('cosmetic_surgery.complications')}>
                                    <TextArea rows={2} />
                                </Form.Item>

                                <Form.Item label={t('cosmetic_surgery.treatment_cost')}>
                                    <Input prefix="VNĐ" />
                                </Form.Item>
                            </Form>
                        </Col>

                        <Col xs={24} md={12}>
                            <div className="flex flex-col gap-3">

                                {/* TITLE */}
                                <div className="text-center font-medium text-gray-700 dark:text-gray-200">
                                    {t('cosmetic_surgery.surgery_map')}
                                </div>

                                {/* MAPPING AREA */}
                                <div className="border-2 border-dashed border-indigo-300 dark:border-indigo-500/40 rounded-xl h-[420px] flex flex-col items-center justify-center text-center px-6">

                                    {/* ICON */}
                                    <div className="text-indigo-200 dark:text-indigo-400/20 mb-4">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="120"
                                            height="120"
                                            fill="currentColor"
                                            viewBox="0 0 256 256"
                                        >
                                            <path d="M128 24a56 56 0 1 0 56 56 56.06 56.06 0 0 0-56-56zm0 96a40 40 0 1 1 40-40 40 40 0 0 1-40 40zm64 32h-8.32A72.13 72.13 0 0 0 128 128a72.13 72.13 0 0 0-55.68 24H64a64 64 0 0 0-64 64 8 8 0 0 0 16 0 48.05 48.05 0 0 1 48-48h128a48.05 48.05 0 0 1 48 48 8 8 0 0 0 16 0 64 64 0 0 0-64-64z" />
                                        </svg>
                                    </div>

                                    {/* DESCRIPTION */}
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                        {t('cosmetic_surgery.map_description')}
                                    </p>

                                    {/* BADGE */}
                                    <span className="text-xs px-3 py-1 rounded-md bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300">
                                        {t('cosmetic_surgery.mapping_area')}
                                    </span>
                                </div>

                            </div>
                        </Col>
                    </Row>

                    <div className="flex justify-end mt-4">
                        <Button type="primary" icon={<Save size={16} />}>
                            {t('cosmetic_surgery.save')}
                        </Button>
                    </div>
                </Card>
            ),
        },
    ];

    return (
        <Layout>
            <Helmet>
                <title>{t('page.cosmetic_surgery')} | Khothi</title>
            </Helmet>
            <div className="flex justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-600 text-white flex items-center justify-center rounded-lg">
                        <Scissors size={20} />
                    </div>

                    <div>
                        <h1 className="text-lg font-semibold dark:text-gray-100 text-000000e0">
                            {t('cosmetic_surgery.page_title')}
                        </h1>

                        <p className="text-sm text-gray-500">
                            {t('cosmetic_surgery.page_subtitle')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <DatePicker
                        defaultValue={dayjs()}
                        format="DD/MM/YYYY"
                        className="min-w-[160px]"
                    />
                </div>
            </div>

            <Tabs items={items} />
        </Layout>
    );
}